import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useProfile } from '@/contexts/ProfileContext';
import { toast } from '@/components/ui/sonner';
import SEOHead from '@/components/SEOHead';
import { MysticalButton } from '@/components/ui/mystical-button';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { profile, updateProfile } = useProfile();
  
  const [name, setName] = React.useState(profile.name || '');
  const [day, setDay] = React.useState(profile.day?.toString() || '');
  const [month, setMonth] = React.useState(profile.month?.toString() || '');
  const [year, setYear] = React.useState(profile.year?.toString() || '');
  const [isRevealing, setIsRevealing] = React.useState(false);

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile({
      name,
      day: parseInt(day),
      month: parseInt(month),
      year: parseInt(year),
    });
    
    setIsRevealing(true);
    setTimeout(() => {
      toast.success('Your cosmic blueprint has been recorded', {
        description: 'All thirteen ancient systems now speak as one.',
      });
      navigate('/profile');
    }, 800);
  };

  const systems = [
    { name: 'Pythagorean', glyph: '△', planet: 'mercury', description: 'The harmony of numbers', path: '/pythagorean' },
    { name: 'Chaldean', glyph: '☽', planet: 'moon', description: 'Ancient Babylonian wisdom', path: '/chaldean' },
    { name: 'Gematria', glyph: '✯', planet: 'saturn', description: 'Hebrew mystical codes', path: '/gematria' },
    { name: 'Western', glyph: '●', planet: 'sun', description: 'Celestial influences', path: '/western' },
    { name: 'Vedic', glyph: '॰', planet: 'jupiter', description: 'Sidereal calculations', path: '/vedic' },
    { name: 'BaZi', glyph: '䷀', planet: 'venus', description: 'Four Pillars of Destiny', path: '/bazi' },
    { name: 'Agrippa', glyph: '※', planet: 'mercury', description: 'Renaissance occult philosophy', path: '/agrippa' },
    { name: 'Enochian', glyph: '⛤', planet: 'mercury', description: 'Angelic language of Dee', path: '/enochian' },
    { name: 'Sepharial', glyph: '⊙', planet: 'saturn', description: 'Astrological degree meanings', path: '/sepharial' },
    { name: 'Eliphas Levi', glyph: '⚹', planet: 'moon', description: 'Tarot & transcendental magic', path: '/eliphas-levi' },
    { name: 'Liber 777', glyph: '∞', planet: 'sun', description: "Crowley's Qabalah", path: '/liber777' },
    { name: 'Abramelin', glyph: '✪', planet: 'jupiter', description: 'Sacred angel magic', path: '/abramelin' },
    { name: 'Plato', glyph: '◯', planet: 'venus', description: 'Sacred geometry & solids', path: '/plato' },
  ];

  return (
    <>
      <SEOHead />
      <div className="min-h-screen sacred-geometry-bg">
        {/* Hero Section - Mystical Opening */}
        <div className="relative overflow-hidden">
          <div className="starfield absolute inset-0 opacity-30"></div>
          
          <div className="container mx-auto px-4 py-16 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              {/* Mystical Title */}
              <div className="mb-8">
                <div className="divider-mystical mb-6"></div>
                <h1 className="font-mystical text-5xl mb-4 tracking-wider">
                  <span className="number-mystical">CALCULATIONS</span>
                </h1>
              </div>

              {/* Initiation Card */}
              <Card className={`card-mystical border-alchemical max-w-2xl mx-auto ${isRevealing ? 'reveal-divination' : ''}`}>
                <div className="p-8">
                  <h2 className="font-mystical text-2xl mb-6 text-center">
                    Begin Your Journey
                  </h2>
                  
                  <form onSubmit={handleProfileSubmit} className="space-y-6">
                    <div>
                      <Label className="font-philosophical text-sm text-gray-600">
                        Your Sacred Name
                      </Label>
                      <Input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="font-ancient text-lg border-alchemical"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="font-philosophical text-sm text-gray-600">
                          Day <span className="glyph-sun text-yellow-600"></span>
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          max="31"
                          value={day}
                          onChange={(e) => setDay(e.target.value)}
                          className="font-ancient text-center"
                          required
                        />
                      </div>
                      <div>
                        <Label className="font-philosophical text-sm text-gray-600">
                          Month <span className="glyph-moon text-gray-400"></span>
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          max="12"
                          value={month}
                          onChange={(e) => setMonth(e.target.value)}
                          className="font-ancient text-center"
                          required
                        />
                      </div>
                      <div>
                        <Label className="font-philosophical text-sm text-gray-600">
                          Year <span className="glyph-saturn text-gray-700"></span>
                        </Label>
                        <Input
                          type="number"
                          min="1900"
                          max="2030"
                          value={year}
                          onChange={(e) => setYear(e.target.value)}
                          className="font-ancient text-center"
                          required
                        />
                      </div>
                    </div>

                    <MysticalButton
                      type="submit"
                      className="w-full"
                    >
                      UNVEIL MY NUMBERS
                    </MysticalButton>
                  </form>
                </div>
              </Card>

              {/* Available Systems Grid */}
              <div className="mt-16">
                <h3 className="font-mystical text-2xl mb-8">Ancient Systems of Wisdom</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
                  {systems.map((system) => (
                    <Card 
                      key={system.name}
                      className={`card-mystical cursor-pointer group hover:shadow-lg transition-all`}
                      onClick={() => navigate(system.path)}
                    >
                      <div className="p-4 text-center">
                        <div className="text-3xl mb-2 sacred-number">{system.glyph}</div>
                        <h4 className="font-mystical text-base mb-1">{system.name}</h4>
                        <p className="font-philosophical text-xs opacity-70">
                          {system.description}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Mystical Quote */}
              <div className="mt-16 golden-spacing">
                <div className="divider-mystical"></div>
                <blockquote className="font-ancient italic text-gray-600 text-lg">
                  "The universe is built on a mathematical plan. 
                  The profound symmetries of nature are the shadows of numbers."
                </blockquote>
                <cite className="font-philosophical text-sm text-gray-500 mt-2 block">
                  — Ancient Hermetic Teaching
                </cite>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;